import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Drawer, Form, Button, SelectPicker, Message, useToaster } from 'rsuite';
import { useDispatch, useSelector } from 'react-redux';
import { fetchFeaturesLeave } from '@/services/feature.service';

interface User {
  _id: string;
  firstname: string;
  lastname: string;
  registrationNumber: string;
  forgotten?: boolean; // Added to handle the forgotten property
}

interface DrawerViewProps {
  open: boolean;
  onClose: () => void;
  onFiltersChange: (filters: any) => void;
  onResetFilters: () => void;
  currentFilters: {
    userId: string;
    validatorId: string;
    status: string;
    type: string;
  };
  users: User[];
  validators: User[];
  usersLoading: boolean;
}

const DrawerView: React.FC<DrawerViewProps> = ({
  open,
  onClose,
  onFiltersChange,
  onResetFilters,
  currentFilters,
  users,
  validators,
  usersLoading
}) => {
  const dispatch = useDispatch();
  const toaster = useToaster();
  const [formValue, setFormValue] = React.useState(currentFilters);
  const { leaveTypes = [], loading: leaveTypesLoading = false } = useSelector(
    (state: any) => state.feature || {}
  );
  const { t } = useTranslation();
  const statusOptions = [
    { label: t('ALL'), value: '' },
    { label: t('PENDING'), value: 'pending' },
    { label: t('ACCEPTED'), value: 'accepted' },
    { label: t('REFUSED'), value: 'refused' }
  ];

  const leaveTypeOptions = React.useMemo(() => {
    if (!leaveTypes?.length) return [{ label: 'All', value: '' }];

    return [
      { label: 'All', value: '' },
      ...leaveTypes.map((leaveType: any) => ({
        label: leaveType.type,
        value: leaveType.type
      }))
    ];
  }, [leaveTypes]);

  const userOptions = React.useMemo(() => {
    return users
      .filter(user => !user.forgotten) // Filter out users with forgotten: true
      .map(user => ({
        label: `${user.firstname} ${user.lastname} (${user.registrationNumber})`,
        value: user._id
      }));
  }, [users]);

  const validatorOptions = React.useMemo(() => {
    return validators
      .filter(validator => !validator.forgotten) // Filter out validators with forgotten: true
      .map(validator => ({
        label: `${validator.firstname} ${validator.lastname} (${validator.registrationNumber})`,
        value: validator._id
      }));
  }, [validators]);

  useEffect(() => {
    if (open) {
      dispatch(fetchFeaturesLeave());
      setFormValue(currentFilters);
    }
  }, [open, currentFilters, dispatch]);

  const handleSubmit = () => {
    const hasFilters = Object.values(formValue).some(value => value !== '' && value !== null);

    if (hasFilters) {
      onFiltersChange({
        userId: formValue.userId,
        validatorId: formValue.validatorId,
        status: formValue.status,
        type: formValue.type
      });
      toaster.push(
        <Message type="success" duration={3000}>
          {t('Filters applied successfully')}
        </Message>,
        { placement: 'topCenter' }
      );
    } else {
      onResetFilters();
    }
    onClose();
  };

  const handleReset = () => {
    setFormValue({
      userId: '',
      validatorId: '',
      status: '',
      type: ''
    });
    onResetFilters();
    toaster.push(
      <Message type="info" duration={3000}>
        {t('Filters have been reset')}
      </Message>,
      { placement: 'topCenter' }
    );
    onClose();
  };

  return (
    <Drawer backdrop="static" size="sm" placement="right" onClose={onClose} open={open}>
      <Drawer.Header>
        <Drawer.Title>{t('Filter Options')}</Drawer.Title>
        <Drawer.Actions>
          <Button onClick={handleReset} appearance="subtle">
            {t('Reset')}
          </Button>
          <Button onClick={handleSubmit} appearance="primary">
            {t('Apply')}
          </Button>
          <Button onClick={onClose} appearance="subtle">
            {t('Cancel')}
          </Button>
        </Drawer.Actions>
      </Drawer.Header>

      <Drawer.Body>
        <Form fluid formValue={formValue} onChange={setFormValue}>
          <Form.Group>
            <Form.ControlLabel>{t('User & Registration Number')}</Form.ControlLabel>
            <Form.Control
              name="userId"
              accepter={SelectPicker}
              data={userOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select user')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Validator')}</Form.ControlLabel>
            <Form.Control
              name="validatorId"
              accepter={SelectPicker}
              data={validatorOptions}
              loading={usersLoading}
              searchable
              placeholder={t('Select validator')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Leave Type')}</Form.ControlLabel>
            <Form.Control
              name="type"
              accepter={SelectPicker}
              data={leaveTypeOptions}
              loading={leaveTypesLoading}
              searchable
              placeholder={t('Select leave type')}
              block
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel>{t('Status')}</Form.ControlLabel>
            <Form.Control
              name="status"
              accepter={SelectPicker}
              data={statusOptions}
              searchable={false}
              placeholder={t('Select status')}
              block
            />
          </Form.Group>
        </Form>
      </Drawer.Body>
    </Drawer>
  );
};

export default DrawerView;
